import Checkbox from 'components/Checkbox/checkbox'
import { Tabs } from 'components/Tabs'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { ReactNode, useState } from 'react'
import { formatInteger } from 'utils/formatting'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { FilterIdentifier } from 'models/saved_view'

type Item = {
  label: string
  value: string | number
  [key: string]: unknown
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type Props<TPage, _TItem> = {
  infiniteQuery: UseInfiniteQueryResult<InfiniteData<TPage>>
  onChange: (arg: Item[]) => void
  renderItem: (arg: Item) => ReactNode
  initialSelectedIds: number[]
  extractItems: (page: TPage) => Item[]
  extractCount: (page: TPage) => number
  filterStoreIdentifier: FilterIdentifier
}

export function MobileSearchSelectView<TPage, TItem>({
  infiniteQuery,
  renderItem,
  onChange,
  initialSelectedIds = [],
  extractItems,
  extractCount,
  filterStoreIdentifier,
}: Props<TPage, TItem>) {
  const [selectedTab, setSelectedTab] = useState('not_selected')

  const data = infiniteQuery.data?.pages.flatMap(extractItems)

  const initialSelected = initialSelectedIds.map((id) => {
    return (
      data?.find((d) => d.value === id) || {
        label: 'unknown',
        value: 'unknown',
      }
    )
  })

  const [selected, setSelected] = useState<Item[]>(initialSelected)
  const selectedCount = selected.length
  const allCount = infiniteQuery?.data
    ? extractCount(infiniteQuery.data?.pages[0])
    : 0

  const handleSelectItem = (item: Item) => {
    const newSelected = [...selected, item]
    setSelected(newSelected)
    onChange(newSelected)
  }

  const handleUnselectItem = (item: Item) => {
    const newSelected = selected.filter((s) => item.value !== s.value)
    setSelected(newSelected)
    onChange(newSelected)
  }

  return (
    <div className="mt-3 mx-2">
      <Tabs
        tabs={[
          {
            key: 'selected',
            title: `Selected (${formatInteger(selectedCount)})`,
          },
          {
            key: 'not_selected',
            title: `Not Selected (${formatInteger(allCount)})`,
          },
        ]}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      <MobileListViewHeader
        sortingOptions={[]}
        filterStoreIdentifier={filterStoreIdentifier}
        tableKey={filterStoreIdentifier}
      />

      <MobileInfiniteScrollView
        extractItems={extractItems}
        infiniteQuery={infiniteQuery}
        className="py-2 flex flex-col"
      >
        {(items) => {
          const displayItems = selectedTab === 'selected' ? selected : items
          return (
            <div className="p-2">
              {displayItems?.map((item) => {
                const checked = selected.some((s) => s.value === item.value)
                return (
                  <div
                    className="bg-sage-50 border-b-2 border-new-company-gray-border p-2 flex"
                    key={item.value}
                    onClick={() => {
                      if (checked) {
                        handleUnselectItem(item)
                      } else {
                        handleSelectItem(item)
                      }
                    }}
                  >
                    <Checkbox
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                      wrapperClassName="mt-1"
                      checked={checked}
                      readOnly
                    />
                    <div className="ml-2">{renderItem(item)}</div>
                  </div>
                )
              })}
            </div>
          )
        }}
      </MobileInfiniteScrollView>
    </div>
  )
}
